import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { 
  UserPlus, 
  Trash2, 
  Shield,
  Mail,
  AlertCircle,
  CheckCircle,
  XCircle,
  Send,
  Key,
  Copy
} from 'lucide-react'
import { api } from '../services/api'
import { useAuth } from '../hooks/useAuth'

interface Admin {
  id: string
  email: string
  is_primary: boolean
  first_login: boolean
  created_at: string
  updated_at?: string
}

interface AdminListResponse {
  items: Admin[]
  total: number
}

export function AdminsPage() {
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [email, setEmail] = useState('')
  const [deleteAdminId, setDeleteAdminId] = useState<string | null>(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [, setResendEmailAdminId] = useState<string | null>(null)
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [passwordAdminId, setPasswordAdminId] = useState<string | null>(null)
  const [temporaryPassword, setTemporaryPassword] = useState('')
  const [generatedPassword, setGeneratedPassword] = useState<string | null>(null)
  const { admin: currentAdmin } = useAuth()
  const queryClient = useQueryClient()

  const { data, isLoading, error } = useQuery<AdminListResponse>({
    queryKey: ['admins'],
    queryFn: async () => {
      const response = await api.get('/admin/admins')
      return response.data
    },
  })

  const createMutation = useMutation({
    mutationFn: async (email: string) => {
      await api.post('/admin/admins', { email })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admins'] })
      setShowCreateModal(false)
      setEmail('')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (adminId: string) => {
      await api.delete(`/admin/admins/${adminId}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admins'] })
      setShowDeleteModal(false)
      setDeleteAdminId(null)
    },
  })

  const resendEmailMutation = useMutation({
    mutationFn: async (adminId: string) => {
      await api.post(`/admin/admins/${adminId}/resend-email`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admins'] })
      setResendEmailAdminId(null)
    },
    onError: () => {
    },
  })

  const setPasswordMutation = useMutation({
    mutationFn: async ({ adminId, password }: { adminId: string; password: string }) => {
      const response = await api.post(`/admin/admins/${adminId}/set-temporary-password`, {
        temporary_password: password
      })
      return response.data
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['admins'] })
      setGeneratedPassword(data.temporary_password)
      setShowPasswordModal(false)
      setPasswordAdminId(null)
      setTemporaryPassword('')
    },
    onError: (error: any) => {
      alert(error.response?.data?.detail || 'Failed to set password')
    },
  })

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email) return
    
    try {
      await createMutation.mutateAsync(email)
    } catch (error: any) {
    }
  }

  const handleDeleteClick = (admin: Admin) => {
    setDeleteAdminId(admin.id)
    setShowDeleteModal(true)
  }

  const handleDeleteConfirm = async () => {
    if (!deleteAdminId) return
    
    try {
      await deleteMutation.mutateAsync(deleteAdminId)
    } catch (error: any) {
    }
  }

  const handleResendEmail = async (adminId: string) => {
    try {
      await resendEmailMutation.mutateAsync(adminId)
    } catch (error: any) {
      // Error already handled in onError
    }
  }

  const handleSetPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!passwordAdminId || !temporaryPassword) return
    
    try {
      await setPasswordMutation.mutateAsync({
        adminId: passwordAdminId,
        password: temporaryPassword
      })
    } catch (error: any) {
      // Error already handled in onError
    }
  }

  const generatePassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*'
    let password = ''
    for (let i = 0; i < 16; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length))
    }
    setTemporaryPassword(password)
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="rounded-md bg-red-50 p-4">
        <div className="flex">
          <AlertCircle className="h-5 w-5 text-red-400" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error loading admins</h3>
            <p className="mt-2 text-sm text-red-700">Failed to load admin list</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Admin Management</h1>
          <p className="mt-1 text-sm text-gray-500">Manage admin accounts</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
        >
          <UserPlus className="h-5 w-5 mr-2" />
          Add Admin
        </button>
      </div>

      {/* Admins Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Email
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Created
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data?.items.map((admin) => {
              const isCurrentUser = currentAdmin?.id === admin.id
              const canDelete = !admin.is_primary && !isCurrentUser
              
              return (
                <tr key={admin.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center min-w-0">
                      <div className="h-10 w-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <Mail className="h-5 w-5 text-red-600" />
                      </div>
                      <div className="ml-4 min-w-0 flex-1 overflow-x-auto scrollbar-hide">
                        <div className="text-sm font-medium text-gray-900 whitespace-nowrap">{admin.email}</div>
                        {admin.is_primary && (
                          <div className="text-xs text-gray-500 flex items-center mt-1 whitespace-nowrap">
                            <Shield className="h-3 w-3 mr-1 flex-shrink-0" />
                            Primary Admin
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      {admin.first_login ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          <AlertCircle className="h-3 w-3 mr-1" />
                          Pending Setup
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Active
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(admin.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      {admin.first_login && !admin.is_primary && (
                        <>
                          <button
                            onClick={() => handleResendEmail(admin.id)}
                            disabled={resendEmailMutation.isPending}
                            className="text-blue-600 hover:text-blue-900 inline-flex items-center disabled:opacity-50"
                            title="Resend setup email"
                          >
                            <Send className="h-4 w-4 mr-1" />
                            Resend Email
                          </button>
                          <button
                            onClick={() => {
                              setPasswordAdminId(admin.id)
                              setShowPasswordModal(true)
                            }}
                            className="text-orange-600 hover:text-orange-900 inline-flex items-center"
                            title="Set temporary password"
                          >
                            <Key className="h-4 w-4 mr-1" />
                            Set Password
                          </button>
                        </>
                      )}
                      {canDelete ? (
                        <button
                          onClick={() => handleDeleteClick(admin)}
                          className="text-red-600 hover:text-red-900 inline-flex items-center"
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Delete
                        </button>
                      ) : (
                        <span className="text-gray-400 text-xs">
                          {admin.is_primary ? 'Protected' : 'Cannot delete yourself'}
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Create Admin Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setShowCreateModal(false)}></div>
            
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full">
              <div className="flex items-center justify-between p-6 border-b">
                <h2 className="text-lg font-semibold text-gray-900">Create New Admin</h2>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>
              
              <form onSubmit={handleCreate} className="p-6">
                <div className="mb-4">
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500"
                    placeholder="admin@example.com"
                    required
                  />
                  <p className="mt-2 text-sm text-gray-500">
                    An email will be sent to this address with instructions to set up the password.
                  </p>
                </div>
                
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={createMutation.isPending}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                  >
                    {createMutation.isPending ? 'Creating...' : 'Create Admin'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setShowDeleteModal(false)}></div>
            
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full">
              <div className="flex items-center justify-between p-6 border-b">
                <h2 className="text-lg font-semibold text-gray-900">Delete Admin</h2>
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>
              
              <div className="p-6">
                <div className="mb-4">
                  <AlertCircle className="h-12 w-12 text-red-600 mx-auto mb-4" />
                  <p className="text-sm text-gray-700 text-center">
                    Are you sure you want to delete this admin account? This action cannot be undone.
                  </p>
                </div>
                
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowDeleteModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteConfirm}
                    disabled={deleteMutation.isPending}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                  >
                    {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Set Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => {
              setShowPasswordModal(false)
              setPasswordAdminId(null)
              setTemporaryPassword('')
            }}></div>
            
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full">
              <div className="flex items-center justify-between p-6 border-b">
                <h2 className="text-lg font-semibold text-gray-900">Set Temporary Password</h2>
                <button
                  onClick={() => {
                    setShowPasswordModal(false)
                    setPasswordAdminId(null)
                    setTemporaryPassword('')
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>
              
              <form onSubmit={handleSetPassword} className="p-6">
                <div className="mb-4">
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                    Temporary Password
                  </label>
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      id="password"
                      value={temporaryPassword}
                      onChange={(e) => setTemporaryPassword(e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500"
                      placeholder="Enter or generate password"
                      required
                      minLength={8}
                    />
                    <button
                      type="button"
                      onClick={generatePassword}
                      className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      Generate
                    </button>
                  </div>
                  <p className="mt-2 text-sm text-gray-500">
                    This password will allow the admin to log in. They will need to set their own password on first login.
                  </p>
                </div>
                
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowPasswordModal(false)
                      setPasswordAdminId(null)
                      setTemporaryPassword('')
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={setPasswordMutation.isPending}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                  >
                    {setPasswordMutation.isPending ? 'Setting...' : 'Set Password'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Generated Password Display Modal */}
      {generatedPassword && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setGeneratedPassword(null)}></div>
            
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full">
              <div className="flex items-center justify-between p-6 border-b">
                <h2 className="text-lg font-semibold text-gray-900">Temporary Password Generated</h2>
                <button
                  onClick={() => setGeneratedPassword(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>
              
              <div className="p-6">
                <div className="mb-4">
                  <AlertCircle className="h-12 w-12 text-orange-600 mx-auto mb-4" />
                  <p className="text-sm text-gray-700 text-center mb-4">
                    Share this password securely with the admin. They can use it to log in and will be prompted to set their own password.
                  </p>
                  <div className="bg-gray-50 p-4 rounded-md">
                    <div className="flex items-center justify-between">
                      <code className="text-lg font-mono text-gray-900">{generatedPassword}</code>
                      <button
                        onClick={() => copyToClipboard(generatedPassword)}
                        className="ml-2 p-2 text-gray-600 hover:text-gray-900"
                        title="Copy to clipboard"
                      >
                        <Copy className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </div>
                
                <div className="flex justify-end">
                  <button
                    onClick={() => setGeneratedPassword(null)}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

